import { Location } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { DialogService } from 'primeng/dynamicdialog';
import { take } from 'rxjs';
import { environment } from '../../../../environment/environment';
import { CanDeactivateType } from '../../../guards/form.guard';
import { UserUpdateModel } from '../../../models/user.model';
import { AlertService } from '../../../services/alert.service';
import { FormService } from '../../../services/form.service';
import { UserService } from '../../../services/user.service';
import { getCountryOptions } from '../../../utils/address.utils';
import { getRandomAvatarColorStyles } from '../../../utils/color.utils';
import { base64ToFile } from '../../../utils/file.utils';
import { fieldRequired, hasUnsavedChanges } from '../../../utils/form.utils';
import { houseNumberValidator } from '../../../validators/house-number.validator';
import { ImageCropperDialogComponent } from '../../shared-components/image-cropper-dialog/image-cropper-dialog.component';
import { TabMenuTabModel } from '../../shared-components/tab-menu/tab-menu.component';

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrl: './user.component.scss',
  standalone: false,
})
export class UserComponent implements OnInit {
  public isLoading = true;
  public userForm: FormGroup;
  public activeTab = 'account';
  private initialFormValues: UserUpdateModel;
  public avatarStyles: string;
  public avatarLoaded: boolean = false;
  public tabs: TabMenuTabModel[] = [
    {
      key: 'account',
      label: 'Nutzerdaten',
      iconClass: 'fa-user',
    },
    {
      key: 'bookings',
      label: 'Buchungen',
      iconClass: 'fa-book-open-cover',
    },
    {
      key: 'appointments',
      label: 'Termine',
      iconClass: 'fa-calendar-heart',
    },
  ];

  /* add window.onbeforeunload to warn the user if the form has unsaved changes */
  @HostListener('window:beforeunload', ['$event'])
  public reloadNotification($event: any): void {
    if (hasUnsavedChanges(this.initialFormValues, this.userForm.value)) {
      $event.returnValue =
        'Es gibt ungespeicherte Änderungen. Wenn Sie die Seite verlassen, gehen Daten verloren.';
    }
  }

  public selectedImage: string | null = null;
  public countryOptions = [];

  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef;

  constructor(
    public userService: UserService,
    private location: Location,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private formService: FormService,
    private alertService: AlertService,
    private dialogService: DialogService
  ) {
    this.createForm();
    const randomAvatarStyles = getRandomAvatarColorStyles();
    this.avatarStyles = `background-color: ${randomAvatarStyles.background}; color: ${randomAvatarStyles.font}`;
    this.countryOptions = getCountryOptions();
  }

  public minDateBirthdate = moment().subtract(100, 'years').toDate();
  public maxDateBirthdate = moment().subtract(16, 'years').toDate();
  public defaultDateBirthdate = moment().subtract(18, 'years').toDate();
  public fieldRequired = fieldRequired;

  public salutationOptions = [
    { value: null, label: 'Keine' },
    { value: 'Herr', label: 'Herr' },
    { value: 'Frau', label: 'Frau' },
  ];

  public getUserInitials(): string {
    const firstname = this.userForm.get('firstname').value;
    const lastname = this.userForm.get('lastname').value;
    return `${firstname.charAt(0)}${lastname.charAt(0)}`.toUpperCase();
  }

  public get avatar(): string | File {
    if (this.selectedImage) {
      return this.selectedImage;
    }
    if (this.userService.currentUser$.value?.avatarPath) {
      return (
        environment.authority_short +
        this.userService.currentUser$.value?.avatarPath
      );
    }
    return null;
  }

  public ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe(params => {
      if (params['tab']) {
        this.activeTab = params['tab'];
      }
    });
    if (!this.userService.currentUser$.value) {
      this.location.back();
      return;
    }
    this.getUser();
  }

  private getUser() {
    const userId = this.userService.currentUser$.value.id;
    this.userService
      .getUserById(userId)
      .pipe(take(1))
      .subscribe({
        next: user => {
          this.userForm.patchValue(user);
          this.userForm.patchValue(user.address);
          this.initialFormValues = this.userForm.value;
          this.isLoading = false;
        },
        error: error => {
          console.error('Error fetching user', error);
          this.location.back();
        },
      });
  }

  private createForm() {
    this.userForm = new FormGroup({
      salutation: new FormControl('', []),
      firstname: new FormControl('', [Validators.required]),
      lastname: new FormControl('', [Validators.required]),
      birthdate: new FormControl('', []),
      phoneNumber: new FormControl('', []),
      street: new FormControl('', []),
      houseNumber: new FormControl('', [houseNumberValidator()]),
      zipCode: new FormControl('', [Validators.minLength(3)]),
      city: new FormControl('', []),
      country: new FormControl('', []),
      email: new FormControl({ value: '', disabled: true }, []),
      bedMember: new FormControl('', []),
      bedMemberNumber: new FormControl('', []),
      jobTitle: new FormControl('', []),
      avatar: new FormControl(null, []),
    });
    this.subscribeToBedMemberChanges();
  }

  private subscribeToBedMemberChanges() {
    this.userForm.get('bedMember').valueChanges.subscribe(value => {
      if (value) {
        this.userForm
          .get('bedMemberNumber')
          .setValidators([Validators.required]);
      } else {
        this.userForm.get('bedMemberNumber').clearValidators();
      }
      this.userForm.get('bedMemberNumber').updateValueAndValidity();
    });
  }

  public onOpenUploadDialog() {
    this.fileInput.nativeElement.click();
  }

  public onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate the file type
      const allowedTypes = [
        'image/jpeg',
        'image/png',
        'image/jpg',
        'image/webp',
      ];
      if (!allowedTypes.includes(file.type)) {
        this.alertService.showErrorAlert(
          'Ungültiger Dateityp. Bitte lade nur Bilder im Format JPG, PNG oder WEBP hoch.'
        );
        // remove the file from the input
        input.files = null;
        return;
      }

      this.openImageCropperDialog(event);
    }
  }

  private openImageCropperDialog(event: Event): void {
    const dialogRef = this.dialogService.open(ImageCropperDialogComponent, {
      header: 'Bild zuschneiden',
      width: '500px',
      breakpoints: {
        '640px': '90vw',
      },
      contentStyle: { overflow: 'auto' },
      data: {
        imageChangedEvent: event,
        aspectRatio: 1 / 1,
        roundCropper: true,
        height: 200,
      },
    });

    dialogRef.onClose.pipe(take(1)).subscribe((croppedImage: string) => {
      if (croppedImage) {
        this.selectedImage = croppedImage;
        const croppedImageFile = base64ToFile(croppedImage, 'cropped-image');
        this.userForm.get('avatar')?.setValue(croppedImageFile);
      }
    });
  }

  public onTabChange(tab: TabMenuTabModel): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        tab: tab.key,
      },
    });
  }

  public onSubmit() {
    this.formService.markFormGroupTouched(this.userForm);
    if (this.userForm.invalid) {
      return;
    }
    const user: UserUpdateModel = {
      id: this.userService.currentUser$.value.id,
      salutation: this.userForm.get('salutation').value,
      firstName: this.userForm.get('firstname').value,
      lastName: this.userForm.get('lastname').value,
      email: this.userService.currentUser$.value.email,
      birthdate: this.userForm.get('birthdate').value,
      phoneNumber: this.userForm.get('phoneNumber').value,
      street: this.userForm.get('street').value,
      houseNumber: this.userForm.get('houseNumber').value,
      zipCode: this.userForm.get('zipCode').value,
      city: this.userForm.get('city').value,
      country: this.userForm.get('country').value,
      bedMember: this.userForm.get('bedMember').value,
      bedMemberNumber:
        this.userForm.get('bedMember').value === true
          ? this.userForm.get('bedMemberNumber').value
          : null,
      jobTitle: this.userForm.get('jobTitle').value,
      avatar: this.userForm.get('avatar').value,
    };
    this.isLoading = true;

    this.userService
      .updateUser(user)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.initialFormValues = this.userForm.value;
          // update first and last name in the user service
          const currentUser = this.userService.currentUser$.value;
          currentUser.firstname = user.firstName;
          currentUser.lastname = user.lastName;
          currentUser.avatar = this.selectedImage;
          this.userService.currentUser$.next(currentUser);

          //set form as pristine
          this.userForm.markAsPristine();
          this.userForm.markAsUntouched();
          this.alertService.showSuccessAlert(
            'Deine Daten wurden erfolgreich aktualisiert.'
          );
        },
        error: error => {
          console.error('Error updating user', error);
        },
      });
  }

  public formHasUnsavedChanges(): boolean {
    return hasUnsavedChanges(this.initialFormValues, this.userForm.value);
  }

  /**
   * checks if the form has unsaved changes amd asks the user if he wants to leave the page
   * @returns CanDeactivateType
   */
  public canDeactivate(): CanDeactivateType {
    if (this.isLoading) {
      return true;
    }

    return this.formService.confirmDeactivation(
      this.userForm.value,
      this.initialFormValues
    );
  }
}
